import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Checkbox, Badge } from 'flowbite-react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { Project, ProjectLabel } from '../../models';
import { Changes } from '../../services';
import { Button, Icon } from '..';
import {
    loadBoard,
    loadProject,
    updateChange,
    updateProject,
    useAppDispatch,
} from '../../redux';

export type SuggestedLabel = {
    key: string;
    name: string;
    color: string;
    action: 'reuse' | 'rename' | 'create';
    existingId?: string | null;
};

export type SuggestedAssignment = {
    changeId: string;
    labelKey: string;
    slug?: number;
    title?: string;
};

export type LabelSuggestion = {
    labels: SuggestedLabel[];
    assignments: SuggestedAssignment[];
    counts?: Record<string, number>;
    notes?: string;
    unassignedCount?: number;
};

export type SuggestLabelsModalProps = {
    show: boolean;
    boardId?: string | null;
    project?: Project | null;
    onClose: () => void;
};

function actionBadgeColor(action: SuggestedLabel['action']): 'info' | 'warning' | 'success' {
    if (action === 'create')
        return 'success';
    if (action === 'rename')
        return 'warning';
    return 'info';
}

function SuggestLabelsModal(props: SuggestLabelsModalProps) {
    const { show, boardId, project, onClose } = props;
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const [loading, setLoading] = useState(false);
    const [applying, setApplying] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [unlabeledOnly, setUnlabeledOnly] = useState(true);
    const [suggestion, setSuggestion] = useState<LabelSuggestion | null>(null);
    const [excludedKeys, setExcludedKeys] = useState<Set<string>>(new Set());

    const visibleLabels = useMemo(() => {
        return (suggestion?.labels || []).filter(label => !excludedKeys.has(label.key));
    }, [suggestion, excludedKeys]);

    const visibleAssignments = useMemo(() => {
        const keys = new Set(visibleLabels.map(label => label.key));
        return (suggestion?.assignments || []).filter(row => keys.has(row.labelKey));
    }, [suggestion, visibleLabels]);

    const counts = useMemo(() => {
        const next: Record<string, number> = {};
        for (const label of visibleLabels)
            next[label.name] = 0;
        for (const row of visibleAssignments) {
            const label = visibleLabels.find(item => item.key === row.labelKey);
            if (label)
                next[label.name] = (next[label.name] || 0) + 1;
        }
        return next;
    }, [visibleLabels, visibleAssignments]);

    const loadSuggestion = async () => {
        if (!boardId)
            return;

        setLoading(true);
        setError(null);
        try {
            const response = await Changes.suggestLabels({
                board_id: boardId,
                unlabeled_only: unlabeledOnly,
                max_labels: 10,
            });
            setSuggestion(response.suggestion as LabelSuggestion);
            setExcludedKeys(new Set());
        } catch (err: any) {
            setSuggestion(null);
            setError(err?.message || t('projects.suggest_labels_error'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!show) {
            setSuggestion(null);
            setError(null);
            setExcludedKeys(new Set());
            return;
        }
        loadSuggestion();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, boardId]);

    const toggleLabel = (key: string) => {
        setExcludedKeys(prev => {
            const next = new Set(prev);
            if (next.has(key))
                next.delete(key);
            else
                next.add(key);
            return next;
        });
    };

    const applySuggestion = async () => {
        if (!project || !suggestion || !visibleLabels.length)
            return;

        setApplying(true);
        setError(null);
        try {
            const touchedIds = new Set(
                visibleLabels
                    .filter(label => label.action !== 'create' && label.existingId)
                    .map(label => String(label.existingId))
            );

            const preserved = (project.labels || [])
                .filter((label: ProjectLabel) => !touchedIds.has(String(label._id)))
                .map((label: ProjectLabel) => ({
                    _id: label._id,
                    name: label.name,
                    color: label.color,
                }));

            const keyToId = new Map<string, string>();
            const proposed = visibleLabels.map(label => {
                const _id = label.action === 'create' || !label.existingId
                    ? uuidv4()
                    : String(label.existingId);
                keyToId.set(label.key, _id);
                return {
                    _id,
                    name: label.name.trim(),
                    color: label.color,
                };
            });

            await dispatch(updateProject({
                ...project,
                labels: [...preserved, ...proposed],
            }));

            for (const row of visibleAssignments) {
                const labelId = keyToId.get(row.labelKey);
                if (!labelId)
                    continue;
                await dispatch(updateChange({
                    _id: row.changeId,
                    labelIds: [labelId],
                }, /*patch*/true, /*optimistic*/true));
            }

            if (project._id)
                await dispatch(loadProject(project._id));
            if (boardId)
                await dispatch(loadBoard(boardId));

            onClose();
        } catch (err: any) {
            setError(err?.message || t('projects.suggest_labels_apply_error'));
        } finally {
            setApplying(false);
        }
    };

    return (
        <Modal show={show} onClose={onClose} size="3xl">
            <Modal.Header>
                <span className="inline-flex items-center gap-2">
                    <Icon type="magic" size={4} />
                    {t('projects.suggest_labels_title')}
                </span>
            </Modal.Header>
            <Modal.Body>
                <div className="space-y-4">
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                        {t('projects.suggest_labels_intro')}
                    </p>

                    <div className="flex flex-wrap items-center gap-3">
                        <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                            <Checkbox
                                checked={unlabeledOnly}
                                onChange={(e) => setUnlabeledOnly(e.target.checked)}
                            />
                            {t('projects.suggest_labels_unlabeled_only')}
                        </label>
                        <Button
                            small
                            color="secondary"
                            icon={loading ? 'loading' : 'magic'}
                            title={t('projects.suggest_labels_refresh') || ''}
                            loading={loading}
                            disabled={loading || applying}
                            onClick={loadSuggestion}
                        />
                    </div>

                    {error && (
                        <div className="rounded border border-red-200 bg-red-50 dark:bg-red-950/30 px-3 py-2 text-sm text-red-700">
                            {error}
                        </div>
                    )}

                    {loading && !suggestion && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">{t('projects.suggest_labels_loading')}</p>
                    )}

                    {suggestion && (
                        <>
                            {suggestion.notes && (
                                <p className="text-xs italic text-gray-500 dark:text-gray-400">{suggestion.notes}</p>
                            )}

                            <div>
                                <h4 className="mb-2 text-sm font-semibold text-gray-800 dark:text-gray-100">
                                    {t('projects.suggest_labels_catalog')}
                                    <span className="ml-1 font-normal text-gray-500 dark:text-gray-400">
                                        ({visibleLabels.length})
                                    </span>
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                    {(suggestion.labels || []).map(label => {
                                        const excluded = excludedKeys.has(label.key);
                                        return (
                                            <button
                                                key={label.key}
                                                type="button"
                                                className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-xs ${
                                                    excluded ? 'opacity-40 line-through' : ''
                                                }`}
                                                style={{
                                                    backgroundColor: label.color,
                                                    color: ProjectLabel.textColor(label.color),
                                                }}
                                                onClick={() => toggleLabel(label.key)}
                                                title={t('projects.suggest_labels_toggle_hint') || ''}
                                            >
                                                <Icon type="tag" size={3} color="inherit" />
                                                <span>{label.name}</span>
                                                <span className="opacity-90">({counts[label.name] || 0})</span>
                                                <Badge
                                                    color={actionBadgeColor(label.action)}
                                                    className="ml-1 !bg-white/90 !text-gray-800 dark:text-gray-100"
                                                >
                                                    {t(`projects.suggest_labels_action_${label.action}`)}
                                                </Badge>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div>
                                <h4 className="mb-2 text-sm font-semibold text-gray-800 dark:text-gray-100">
                                    {t('projects.suggest_labels_assignments')}
                                    <span className="ml-1 font-normal text-gray-500 dark:text-gray-400">
                                        ({visibleAssignments.length})
                                    </span>
                                </h4>
                                {(suggestion.unassignedCount || 0) > 0 && (
                                    <p className="mb-2 text-xs text-amber-700">
                                        {t('projects.suggest_labels_unassigned', {
                                            count: suggestion.unassignedCount,
                                        })}
                                    </p>
                                )}
                                <ul className="max-h-56 space-y-1 overflow-y-auto pr-1">
                                    {visibleAssignments.slice(0, 80).map(row => {
                                        const label = visibleLabels.find(item => item.key === row.labelKey);
                                        return (
                                            <li
                                                key={row.changeId}
                                                className="flex items-center gap-2 rounded border border-gray-200 px-2 py-1 text-sm dark:border-gray-600"
                                            >
                                                <span className="font-mono text-xs text-cyan-700">
                                                    #{row.slug}
                                                </span>
                                                <span className="min-w-0 flex-1 truncate text-gray-800 dark:text-gray-100">
                                                    {row.title}
                                                </span>
                                                {label && (
                                                    <span
                                                        className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium"
                                                        style={{
                                                            backgroundColor: label.color,
                                                            color: ProjectLabel.textColor(label.color),
                                                        }}
                                                    >
                                                        {label.name}
                                                    </span>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                                {visibleAssignments.length > 80 && (
                                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                        {t('projects.suggest_labels_truncated', {
                                            count: visibleAssignments.length - 80,
                                        })}
                                    </p>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <div className="flex w-full justify-end gap-2">
                    <Button
                        color="none"
                        title={t('common.cancel') || ''}
                        onClick={onClose}
                        disabled={applying}
                    />
                    <Button
                        color="secondary"
                        icon="check"
                        title={t('projects.suggest_labels_apply') || ''}
                        loading={applying}
                        disabled={applying || loading || !visibleLabels.length || !visibleAssignments.length}
                        onClick={applySuggestion}
                    />
                </div>
            </Modal.Footer>
        </Modal>
    );
}

export default SuggestLabelsModal;
